import {
  BottomTabIcon,
  createBottomTabNavigator,
} from "@react-navigation/bottom-tabs";
import { useLinkTo, useNavigation } from "@react-navigation/native";
import {
  createNativeStackNavigator,
  NativeStackHeaderBackProps,
  NativeStackNavigationOptions,
} from "@react-navigation/native-stack";
import {
  Text,
  useAccentColor,
  useAccessStatusError,
  useAccessStatusLoaded,
  useBrandingSettled,
  useCardStreamLayout,
  useDefaultStreamer,
  useDID,
  usePrimaryColor,
  useSocialShell,
  useStreamplaceStore,
  useTheme,
  useViewerLockedOut,
  withAlpha,
  zero,
} from "@streamplace/components";
import { colors, spacing } from "@streamplace/components/src/lib/theme/tokens";
import { Settings } from "components";
import AccessWall, { AccessConnecting } from "components/access/access-wall";
import { SiteTitleLockup, useNodeTitle } from "components/brand/logo";
import { LogoBrandMenu } from "components/brand/logo-brand-menu";
import Login from "components/login/login";
import LoginModal from "components/login/login-modal";
import PdsHostSelectorModal from "components/login/pds-host-selector-modal";
import { MobileAppBanner } from "components/mobile-app-banner";
import { AboutCategorySettings } from "components/settings/about-category-settings";
import { AccessAdmin } from "components/settings/access-admin";
import { AccountCategorySettings } from "components/settings/account-category-settings";
import { AdvancedCategorySettings } from "components/settings/advanced-category-settings";
import { BackupSettings } from "components/settings/backup-settings";
import { BadgeIssuerPanel } from "components/settings/badge-issuer-panel";
import { BadgeSelectionManager } from "components/settings/badge-selection-manager";
import { BrandingAdmin } from "components/settings/branding-admin";
import { DanmuCategorySettings } from "components/settings/danmu-category-settings";
import KeyManager from "components/settings/key-manager";
import { LanguagesCategorySettings } from "components/settings/languages-category-settings";
import MultistreamManager from "components/settings/multistream-manager";
import { NotificationsCategorySettings } from "components/settings/notifications-category-settings";
import { PrivacyCategorySettings } from "components/settings/privacy-category-settings";
import RecommendationsManager from "components/settings/recommendations-manager";
import { StreamingCategorySettings } from "components/settings/streaming-category-settings";
import WebhookManager from "components/settings/webhook-manager";
import {
  PhoneMenuButton,
  SidebarOverlay,
  SidebarToggle,
} from "components/sidebar/sidebar-overlay";
import {
  FEED_COLUMN_WIDTH,
  streamColumnWidthFor,
} from "components/stream-card/widths";
import UploadProgressIndicator from "components/upload/upload-progress-indicator";
import { useBlueskyNotifications } from "hooks/useBlueskyNotifications";
import usePlatform from "hooks/usePlatform";
import {
  useIsLargeScreen,
  usePhoneMenu,
  useSidebarControl,
} from "hooks/useSidebarControl";
import { Clapperboard, Cog, Home, Video } from "lucide-react-native";
import { useEffect, useState } from "react";
import {
  Platform,
  Pressable,
  StatusBar,
  useWindowDimensions,
  View,
} from "react-native";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import { SFSymbols7_0 } from "sf-symbols-typescript";
import "src/navigation-types";
import AboutScreen from "src/screens/about";
import AppReturnScreen from "src/screens/app-return";
import PopoutChat from "src/screens/chat-popout";
import DanmuOBSScreen from "src/screens/danmu-obs";
import DownloadScreen from "src/screens/download";
import EmbedScreen from "src/screens/embed";
import HomeScreen from "src/screens/home";
import InfoWidgetEmbed from "src/screens/info-widget-embed";
import LaunchGoLive from "src/screens/launch-go-live";
import LiveDashboard from "src/screens/live-dashboard";
import MobileGoLive from "src/screens/mobile-go-live";
import MobileStream from "src/screens/mobile-stream";
import MultiScreen from "src/screens/multi";
import PopoutInfoWidget from "src/screens/popout-info-widget";
import PopoutLivestream from "src/screens/popout-livestream";
import PopoutMultistream from "src/screens/popout-multistream";
import PopoutStreamMonitor from "src/screens/popout-stream-monitor";
import SupportScreen from "src/screens/support";
import UploadScreen, {
  UploadDraftsScreen,
  UploadLivestreamsScreen,
  UploadVideoScreen,
  UploadVideosScreen,
} from "src/screens/upload";
import VideoScreen from "src/screens/video";
import VideoListScreen from "src/screens/video-list";
import VodScreen from "src/screens/vod";
import VodEmbedScreen from "src/screens/vod-embed";
import { useStore } from "store";
import {
  useHydrated,
  useNotificationDestination,
  useNotificationToken,
} from "store/hooks";
import {
  AvatarButton,
  LGAvatarButton,
  NavigationButton,
  UploadButton,
} from "./router";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const Tab = createBottomTabNavigator();
const RootStack = createNativeStackNavigator();
const HomeStack = createNativeStackNavigator();
const VideosStack = createNativeStackNavigator();
const SettingsStack = createNativeStackNavigator();

function useBaseScreenOptions() {
  const z = useTheme();
  const social = useSocialShell();
  return {
    headerShown: true,
    headerTransparent: Platform.OS === "ios",
    headerBackButtonDisplayMode: "minimal" as const,
    // Slim, quiet chrome: 15px medium title on surface0 with a hairline.
    // The social shell has no top bar: the header is the feed column's own
    // 52px title row (19px semibold on the page background).
    headerTitleStyle: {
      fontFamily: z.theme.typography.universal.xl.fontFamily,
      fontSize: social ? 19 : 15,
      fontWeight: "500" as const,
      color: z.theme.colors.text1,
    },
    headerStyle: {
      backgroundColor:
        Platform.OS === "web"
          ? social
            ? z.theme.colors.background
            : z.theme.colors.surface0
          : undefined,
      borderBottomColor: z.theme.colors.borderSubtle,
      borderBottomWidth: 1,
      // Slimmer bar, aligned with the 56px sidebar brand row.
      height: social ? 52 : 56,
    },
  };
}

// The Upload + avatar cluster at the header's right. The social shell keeps
// the avatar in the nav rail instead, so its headers carry nothing.
function HeaderRight({ large = false }: { large?: boolean }) {
  const social = useSocialShell();
  const phoneMenu = usePhoneMenu();
  if (social) return phoneMenu ? <PhoneMenuButton /> : null;
  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <UploadButton />
      {large ? <LGAvatarButton /> : <AvatarButton />}
    </View>
  );
}

// Home navigator (contains home + all general navigation screens)
function HomeNavigator() {
  const title = useNodeTitle();
  const baseScreenOptions = useBaseScreenOptions();
  const social = useSocialShell();
  // On a phone the header is where the social shell's menu button lives.
  const phoneMenu = usePhoneMenu();
  const defaultStreamer = useDefaultStreamer();
  const isNative = Platform.OS !== "web";
  const z = useTheme();
  const did = useDID();

  const headerScreenOptions: NativeStackNavigationOptions = {
    headerShown: !isNative,
    headerLeft: isNative
      ? undefined
      : ({ canGoBack }: NativeStackHeaderBackProps) => (
          <NavigationButton canGoBack={canGoBack} />
        ),
    headerRight: () => <HeaderRight large />,
    ...(isNative && {
      headerTransparent: true,
    }),
    ...(Platform.OS === "ios" && {
      unstable_headerRightItems: () => [
        {
          type: "custom",
          hidesSharedBackground: true,
          element: <HeaderRight large />,
        },
      ],
    }),
    headerTitleStyle: {
      fontFamily: z.theme.typography.universal.xl.fontFamily,
      fontSize: 15,
      fontWeight: "500" as const,
      color: z.theme.colors.text1,
    },
  };

  return (
    <HomeStack.Navigator screenOptions={baseScreenOptions}>
      <HomeStack.Screen
        name="HomeMain"
        component={HomeScreen}
        options={{
          // `title` drives the browser tab: the node's title (runtime
          // branding, else the brand default). The visible web header shows
          // the contextual "Home", not a repeat of it.
          title,
          headerTitle:
            Platform.OS === "ios"
              ? (props) => (
                  <View style={{ flex: 1, alignItems: "flex-start" }}>
                    <Text size="2xl" style={[zero.ml[4]]}>
                      {title}
                    </Text>
                  </View>
                )
              : "Home",
          headerLeft:
            Platform.OS !== "ios"
              ? ({ canGoBack }) => <NavigationButton canGoBack={canGoBack} />
              : undefined,
          headerRight: () => <HeaderRight />,
          // The social shell's front door draws its own column header (the
          // stream card's, or the landing tabs); a phone keeps the header
          // for its menu button.
          headerShown: !social || phoneMenu,
          ...(Platform.OS === "ios" && {
            unstable_headerRightItems: () => [
              {
                type: "custom",
                hidesSharedBackground: true,
                element: <HeaderRight large />,
              },
            ],
          }),
        }}
      />
      <HomeStack.Screen
        name="About"
        component={AboutScreen}
        options={{
          title: "What's Streamplace?",
          ...headerScreenOptions,
        }}
      />
      <HomeStack.Screen
        name="Download"
        component={DownloadScreen}
        options={{ title: "Download", ...headerScreenOptions }}
      />
      <HomeStack.Screen
        name="LiveDashboard"
        component={LiveDashboard}
        options={{ title: "Live streaming", ...headerScreenOptions }}
      />
      <HomeStack.Screen
        name="Login"
        component={Login}
        options={{ title: did ? "Account" : "Login", ...headerScreenOptions }}
      />
      <HomeStack.Screen
        name="Multi"
        component={MultiScreen}
        options={{ title: "Multi-stream", ...headerScreenOptions }}
      />
      <HomeStack.Screen
        name="Support"
        component={SupportScreen}
        options={{ title: "Support", ...headerScreenOptions }}
      />
      {!isNative && (
        <HomeStack.Screen
          name="Upload"
          component={UploadScreen}
          options={{ title: "Upload Video", ...headerScreenOptions }}
        />
      )}
      {!isNative && (
        <HomeStack.Screen
          name="UploadVideo"
          component={UploadVideoScreen}
          options={{ title: "Edit Video", ...headerScreenOptions }}
        />
      )}
      {!isNative && (
        <HomeStack.Screen
          name="UploadDrafts"
          component={UploadDraftsScreen}
          options={{ title: "Drafts", ...headerScreenOptions }}
        />
      )}
      {!isNative && (
        <HomeStack.Screen
          name="UploadLivestreams"
          component={UploadLivestreamsScreen}
          options={{ title: "Livestreams", ...headerScreenOptions }}
        />
      )}
      {!isNative && (
        <HomeStack.Screen
          name="UploadVideos"
          component={UploadVideosScreen}
          options={{ title: "My Videos", ...headerScreenOptions }}
        />
      )}
    </HomeStack.Navigator>
  );
}

// Videos stack navigator (global + per-user VOD listings). Unlike the pushed
// Home screens, these are tab roots, so they always carry their own header.
function VideosNavigator() {
  const baseScreenOptions = useBaseScreenOptions();
  const isNative = Platform.OS !== "web";
  const social = useSocialShell();
  const phoneMenu = usePhoneMenu();

  return (
    <VideosStack.Navigator
      screenOptions={{
        ...baseScreenOptions,
        headerLeft: isNative
          ? undefined
          : ({ canGoBack }: NativeStackHeaderBackProps) => (
              <NavigationButton canGoBack={canGoBack} />
            ),
        headerRight: () => <HeaderRight large />,
        ...(Platform.OS === "ios" && {
          unstable_headerRightItems: () => [
            {
              type: "custom",
              hidesSharedBackground: true,
              element: <HeaderRight large />,
            },
          ],
        }),
      }}
    >
      <VideosStack.Screen
        name="VideoList"
        component={VideoListScreen}
        // The social shell's videos page carries the landing tabs instead;
        // a phone keeps the header for its menu button.
        options={{ title: "Videos", headerShown: !social || phoneMenu }}
      />
      <VideosStack.Screen
        name="UserVideoList"
        component={VideoListScreen}
        options={{ title: "Videos" }}
      />
    </VideosStack.Navigator>
  );
}

// Settings stack navigator
function SettingsNavigator() {
  const baseScreenOptions = useBaseScreenOptions();
  const headerScreenOptions: NativeStackNavigationOptions = {
    ...baseScreenOptions,
    headerTransparent: Platform.OS === "ios",
    headerBackButtonDisplayMode: "minimal" as const,
    headerShown: true,
    // Same Create + avatar cluster as the other navigators, so the header
    // controls don't vanish on Settings screens.
    headerRight: () => <HeaderRight large />,
    ...(Platform.OS === "ios" && {
      unstable_headerRightItems: () => [
        {
          type: "custom",
          hidesSharedBackground: true,
          element: <HeaderRight large />,
        },
      ],
    }),
  };
  return (
    <SettingsStack.Navigator
      initialRouteName="MainSettings"
      screenOptions={{
        ...headerScreenOptions,
      }}
    >
      <SettingsStack.Screen
        name="MainSettings"
        component={Settings}
        options={{ title: "Settings" }}
      />
      <SettingsStack.Screen
        name="AboutCategory"
        component={AboutCategorySettings}
        options={{ title: "About" }}
      />
      <SettingsStack.Screen
        name="AccountCategory"
        component={AccountCategorySettings}
        options={{ title: "Account" }}
      />
      <SettingsStack.Screen
        name="StreamingCategory"
        component={StreamingCategorySettings}
        options={{ title: "Streaming" }}
      />
      <SettingsStack.Screen
        name="WebhooksSettings"
        component={WebhookManager}
        options={{ title: "Webhooks" }}
      />
      <SettingsStack.Screen
        name="BackupSettings"
        component={BackupSettings}
        options={{ title: "Backup" }}
      />
      <SettingsStack.Screen
        name="RecommendationsSettings"
        component={RecommendationsManager}
        options={{ title: "Recommendations" }}
      />
      <SettingsStack.Screen
        name="PrivacyCategory"
        component={PrivacyCategorySettings}
        options={{ title: "Privacy & Security" }}
      />
      <SettingsStack.Screen
        name="NotificationsCategory"
        component={NotificationsCategorySettings}
        options={{ title: "Notifications" }}
      />
      <SettingsStack.Screen
        name="DanmuCategory"
        component={DanmuCategorySettings}
        options={{ title: "Danmu" }}
      />
      <SettingsStack.Screen
        name="AdvancedCategory"
        component={AdvancedCategorySettings}
        options={{ title: "Advanced" }}
      />
      <SettingsStack.Screen
        name="MultistreamCategory"
        component={MultistreamManager}
        options={{ title: "Multistream" }}
      />
      <SettingsStack.Screen
        name="LanguagesCategory"
        component={LanguagesCategorySettings}
        options={{ title: "Languages" }}
      />
      <SettingsStack.Screen
        name="KeyManagement"
        component={KeyManager}
        options={{ title: "Key Manager" }}
      />
      <SettingsStack.Screen
        name="BadgeSelection"
        component={BadgeSelectionManager}
        options={{ title: "Badges" }}
      />
      <SettingsStack.Screen
        name="BadgeIssuer"
        component={BadgeIssuerPanel}
        options={{ title: "Issue Badges" }}
      />
      <SettingsStack.Screen
        name="BrandingAdmin"
        component={BrandingAdmin}
        options={{ title: "Branding" }}
      />
      <SettingsStack.Screen
        name="AccessAdmin"
        component={AccessAdmin}
        options={{ title: "Access" }}
      />
    </SettingsStack.Navigator>
  );
}

const IOS_ICONS: Record<string, SFSymbols7_0> = {
  Home: "house.fill",
  Videos: "play.rectangle.fill",
  GoLive: "video.fill",
  Settings: "gearshape.fill",
};
const ANDROID_ICONS = {
  Home: "home",
  Videos: "video_library",
  GoLive: "videocam",
  Settings: "settings",
};

const getIcon = (
  name: keyof typeof IOS_ICONS | keyof typeof ANDROID_ICONS,
): BottomTabIcon => {
  if (Platform.OS === "ios") {
    return {
      type: "sfSymbol",
      name: IOS_ICONS[name],
    };
  } else {
    return {
      type: "materialSymbol",
      name: ANDROID_ICONS[name],
    };
  }
};

// Tab navigator (main app sections, navigation on web is handled in sidebar)
function TabNavigator() {
  const { isNative, isBrowser } = usePlatform();
  const socialShell = useSocialShell();
  const phoneMenu = usePhoneMenu();
  const accentColor = useAccentColor();
  const primaryColor = usePrimaryColor();
  const isLargeScreen = useIsLargeScreen();
  const z = useTheme();

  return (
    <Tab.Navigator
      screenOptions={{
        lazy: true,
        headerShown: false,
        // Switching tabs, the navigator keeps the previous tab's scene
        // mounted and visible beneath the new one until its (zero-length)
        // transition settles, and again for a frame whenever that effect
        // re-runs, as it does when the new tab's nested navigator
        // initializes. With transparent scenes the old page showed through
        // the new one: the "flash back to the previous page, twice" on
        // every tab change on web. An opaque scene covers it.
        sceneStyle: { backgroundColor: z.theme.colors.background },
        // Hide tab bar on web and < 800px; the social shell has no bottom
        // bar at all (the stream page is a fixed player/chat/composer stack).
        tabBarStyle: socialShell
          ? { display: "none" }
          : isNative
            ? {
                backgroundColor: z.theme.colors.surface1,
                borderTopColor: z.theme.colors.borderSubtle,
              }
            : !isLargeScreen
              ? {
                  backgroundColor: z.theme.colors.surface1,
                  borderTopColor: z.theme.colors.borderSubtle,
                }
              : { display: "none" },
        tabBarActiveTintColor:
          accentColor || primaryColor || z.theme.colors.primary,
        tabBarInactiveTintColor: z.theme.colors.text3,
        headerTitleStyle: {
          fontFamily: z.theme.typography.universal["2xl"].fontFamily,
        },
        headerStyle: {
          backgroundColor: z.theme.colors.background,
        },
      }}
    >
      <Tab.Screen
        name="HomeTab"
        component={HomeNavigator}
        options={{
          title: "Home",
          ...(isNative
            ? {
                tabBarIcon: getIcon("Home"),
              }
            : {
                tabBarIcon: ({ color, size }) => (
                  <Home size={size} color={color} />
                ),
              }),
        }}
      />
      <Tab.Screen
        name="VideosTab"
        component={VideosNavigator}
        options={{
          title: "Videos",
          ...(isNative
            ? {
                tabBarIcon: getIcon("Videos"),
              }
            : {
                tabBarIcon: ({ color, size }) => (
                  <Clapperboard size={size} color={color} />
                ),
              }),
        }}
      />
      <Tab.Screen
        name="GoLiveTab"
        component={LaunchGoLive}
        options={{
          title: "Go Live",
          ...(isNative
            ? {
                tabBarIcon: getIcon("GoLive"),
              }
            : {
                tabBarIcon: ({ color, size }) => (
                  <Video size={size} color={color} />
                ),
              }),
          headerShown: !socialShell || phoneMenu,
          headerTransparent: true,
        }}
      />
      <Tab.Screen
        name="SettingsTab"
        component={SettingsNavigator}
        options={{
          title: "Settings",
          ...(isNative
            ? {
                tabBarIcon: getIcon("Settings"),
              }
            : {
                tabBarIcon: ({ color, size }) => (
                  <Cog size={size} color={color} />
                ),
              }),
          headerShown: false,
        }}
      />
    </Tab.Navigator>
  );
}

// Walk the (possibly nested) navigation state to the deepest active route, so
// checks against leaf screen names like "LiveDashboard" resolve correctly —
// getState().routes[index].name alone only yields the top-level route
// (e.g. "MainTabs"), so nested screens never match.
function getActiveLeafRouteName(state: any): string | undefined {
  if (!state?.routes) return undefined;
  const route = state.routes[state.index ?? 0];
  if (route?.state) return getActiveLeafRouteName(route.state) ?? route.name;
  return route?.name;
}

export default function Shell() {
  const { isNative } = usePlatform();
  const sidebar = useSidebarControl();
  const navigation = useNavigation();
  const hydrate = useStore((state) => state.hydrate);
  const initPushNotifications = useStore(
    (state) => state.initPushNotifications,
  );
  const registerNotificationToken = useStore(
    (state) => state.registerNotificationToken,
  );
  const clearNotification = useStore((state) => state.clearNotification);
  const pollMySegments = useStore((state) => state.pollMySegments);
  const showLoginModal = useStore((state) => state.showLoginModal);
  const closeLoginModal = useStore((state) => state.closeLoginModal);
  const showPdsModal = useStore((state) => state.showPdsModal);
  const openPdsModal = useStore((state) => state.openPdsModal);
  const closePdsModal = useStore((state) => state.closePdsModal);
  const loginAction = useStore((state) => state.login);
  const openLoginLink = useStore((state) => state.openLoginLink);
  const z = useTheme();
  const baseScreenOptions = useBaseScreenOptions();

  // Top-level hydration and initialization
  useEffect(() => {
    hydrate();
    initPushNotifications();
  }, []);

  const notificationToken = useNotificationToken();
  const did = useStore((state) => state.oauthSession?.did);
  const hydrated = useHydrated();

  // Role-based access: hold a blank frame until the node has told us whether
  // the caller may use it, then either wall them off or render the app.
  // Never render the app on a timeout: on a private node that would show a
  // screen whose every request fails. A node that doesn't answer at all gets
  // a "connecting" state with a retry instead.
  const accessStatusLoaded = useAccessStatusLoaded();
  const accessStatusError = useAccessStatusError();
  const viewerLockedOut = useViewerLockedOut();
  // Two more things the first frame waits for (bounded by the same timeout):
  // the OAuth session restore, so a signed-in viewer never sees the wall
  // that the anonymous status answer would paint; and branding, so the
  // default mark and title never flash before the node's own.
  const sessionRestoring = useStreamplaceStore(
    (s) => s.oauthSession === undefined,
  );
  const brandingSettled = useBrandingSettled();
  const brokerSettled = useStore((state) => state.brokerSettled);
  const [bootTimedOut, setBootTimedOut] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setBootTimedOut(true), 6000);
    return () => clearTimeout(handle);
  }, []);
  const [accessTimedOut, setAccessTimedOut] = useState(false);
  // Once the app has painted it stays painted. The gates below hold the
  // first frame until the node has answered for the caller; they are not
  // re-applied when the answer goes stale later (a session arriving from
  // the network's app, a sign-in, a token refresh re-fetch), because that
  // would unmount the navigator, and a navigator that unmounts loses its
  // state: on remount it starts at its default route, so a viewer who
  // opened a streamer's page found themselves on the front page with the
  // URL quietly rewritten. Only the access wall replaces a running app.
  const [booted, setBooted] = useState(false);
  const bootReady =
    hydrated &&
    accessStatusLoaded &&
    ((!sessionRestoring && brandingSettled) || bootTimedOut);
  useEffect(() => {
    if (bootReady) setBooted(true);
  }, [bootReady]);
  useEffect(() => {
    if (accessStatusLoaded) {
      setAccessTimedOut(false);
      return;
    }
    const handle = setTimeout(() => setAccessTimedOut(true), 6000);
    return () => clearTimeout(handle);
  }, [accessStatusLoaded]);

  // Re-register when the token changes OR once the logged-in DID resolves, so a
  // token acquired before the OAuth session finishes restoring still gets its
  // repoDID association registered (otherwise the user is excluded from
  // follower livestream notifications).
  useEffect(() => {
    if (notificationToken) {
      registerNotificationToken();
    }
  }, [notificationToken, did]);

  // Handle incoming push notification routing
  const notificationDestination = useNotificationDestination();
  const linkTo = useLinkTo();

  useEffect(() => {
    if (notificationDestination) {
      linkTo(notificationDestination);
      clearNotification();
    }
  }, [notificationDestination]);

  // Poll for live streamers
  useEffect(() => {
    let handle: NodeJS.Timeout;
    handle = setInterval(() => {
      pollMySegments();
    }, 2500);
    pollMySegments();
    return () => clearInterval(handle);
  }, []);

  useBlueskyNotifications();

  // Track current route
  const [currentRouteName, setCurrentRouteName] = useState<string | undefined>(
    () => {
      try {
        return getActiveLeafRouteName(navigation.getState());
      } catch {
        return undefined;
      }
    },
  );

  useEffect(() => {
    const update = () => {
      const name = getActiveLeafRouteName(navigation.getState());
      if (name) setCurrentRouteName(name);
    };
    update(); // seed on mount so direct loads (e.g. a stream URL) resolve now
    const unsubscribe = navigation.addListener("state", update);
    return unsubscribe;
  }, [navigation]);

  // Detail views (watching a stream or video) take the sidebar out of the flow
  // so it opens as an overlay drawer over dimmed content instead of pushing.
  const setOverlay = useStore((state) => state.setOverlay);
  const closeDrawer = useStore((state) => state.closeDrawer);
  const mobileMenuOpen = useStore((state) => state.mobileMenuOpen);
  // The card stream layout is a feed page with the nav beside it, not a
  // full-bleed player, so it keeps the sidebar in flow.
  const cardStreamLayout = useCardStreamLayout();
  const socialShell = useSocialShell();
  const { width: windowWidth } = useWindowDimensions();
  // A video page reports itself while mounted (the front door showing the
  // default video is one the route name doesn't reveal).
  const videoPage = useStore((state) => state.videoPage);
  const isDetailView =
    (currentRouteName === "Stream" && !cardStreamLayout) ||
    currentRouteName === "Video" ||
    currentRouteName === "Vod" ||
    videoPage;
  // Video pages get a YouTube-style sticky translucent header the content
  // scrolls under; the livestream keeps its own solid header.
  const isVodDetail =
    currentRouteName === "Video" || currentRouteName === "Vod" || videoPage;
  useEffect(() => {
    setOverlay(isDetailView);
  }, [isDetailView, setOverlay]);

  // Animate content margin when sidebar is active (web only). In overlay mode
  // the content stays full width (margin 0) and the drawer floats over it.
  const animatedContentStyle = useAnimatedStyle(() => {
    if (isNative || !sidebar.isActive) {
      return { marginLeft: 0 };
    }
    return {
      marginLeft: sidebar.animatedContentMargin.value,
    };
  });

  // Scrim that dims content behind the overlay drawer.
  const scrimStyle = useAnimatedStyle(() => ({
    opacity: sidebar.animatedScrim.value,
  }));

  // Social shell (web, docked sidebar): the nav + feed + chat cluster is
  // centered in the window and the content is a fixed-width column — feed
  // + hairline + chat on stream pages, the 600px feed alone elsewhere —
  // rather than filling everything right of the rail.
  // Tool screens (the creator dashboard, go-live) need the whole width and
  // stay out of the column.
  const fullWidthRoute =
    currentRouteName === "LiveDashboard" || currentRouteName === "MobileGoLive";
  const socialDocked =
    socialShell &&
    !isNative &&
    sidebar.isActive &&
    !sidebar.overlay &&
    !fullWidthRoute;
  // The stream card layout reports itself while mounted; the route name
  // alone misfires (e.g. right after the return from login).
  const isStreamPage = useStore((state) => state.wideColumn);
  const socialClusterWidth =
    sidebar.contentMargin + streamColumnWidthFor(windowWidth);
  const socialOffset = socialDocked
    ? Math.max(0, Math.floor((windowWidth - socialClusterWidth) / 2))
    : 0;
  const socialColumnWidth = socialDocked
    ? Math.min(
        isStreamPage ? streamColumnWidthFor(windowWidth) : FEED_COLUMN_WIDTH,
        Math.max(320, windowWidth - socialOffset - sidebar.contentMargin),
      )
    : undefined;

  if (!booted) {
    if (!hydrated) {
      return <View />;
    }
    if (!accessStatusLoaded) {
      return accessTimedOut || accessStatusError ? (
        <AccessConnecting error={accessStatusError} />
      ) : (
        <View />
      );
    }
    if ((sessionRestoring || !brandingSettled) && !bootTimedOut) {
      return <View />;
    }
  }

  // The login + PDS modals live here (not inside the navigator) so the access
  // wall can still open them when the node is private.
  const authModals = (
    <>
      <LoginModal
        visible={showLoginModal}
        onClose={closeLoginModal}
        onOpenPdsModal={openPdsModal}
      />
      <PdsHostSelectorModal
        open={showPdsModal}
        onOpenChange={closePdsModal}
        onSubmit={(pdsHost) => {
          closePdsModal();
          loginAction(pdsHost, openLoginLink);
        }}
      />
    </>
  );

  if (viewerLockedOut) {
    // The anonymous answer says "wall", but the network's broker may be
    // about to hand over a session that is on the list: hold the frame for
    // that answer (bounded by the same timeout) rather than flash the wall.
    // The open-node case above doesn't wait — the page paints signed out
    // and the composer fills in when the broker answers.
    if (!brokerSettled && !bootTimedOut) {
      return <View />;
    }
    return (
      <View style={{ flex: 1 }}>
        <StatusBar barStyle="light-content" />
        <AccessWall />
        {authModals}
      </View>
    );
  }

  return (
    // Painted explicitly: in the social shell the navigator only covers its
    // fixed-width column, so the page background has to come from here.
    <View style={{ flex: 1, backgroundColor: z.theme.colors.background }}>
      <StatusBar barStyle="light-content" />
      {!isNative && <SidebarOverlay />}
      {!isNative && <MobileAppBanner />}
      <Animated.View
        style={[
          { flex: 1, backgroundColor: z.theme.colors.background },
          animatedContentStyle,
          socialDocked && { paddingLeft: socialOffset },
        ]}
      >
        <View
          style={
            socialDocked
              ? {
                  flex: 1,
                  alignSelf: "flex-start",
                  width: socialColumnWidth,
                  borderRightWidth: isStreamPage ? 0 : 1,
                  borderRightColor: z.theme.colors.borderSubtle,
                }
              : { flex: 1 }
          }
        >
          <RootStack.Navigator
            screenOptions={{
              // Reuse the shared chrome (surface0 + hairline, 15px title) so
              // pushed screens like Stream don't render a differently-colored
              // header than the HomeStack.
              ...baseScreenOptions,
              headerShown: !isNative,
              headerLeft: ({ canGoBack }) => (
                <NavigationButton canGoBack={canGoBack} />
              ),
              headerRight: () => <HeaderRight large />,
              ...(isNative && {
                headerTransparent: true,
              }),
            }}
          >
            {/* Main tabs (initial screen for all platforms) */}
            <RootStack.Screen
              name="MainTabs"
              component={TabNavigator}
              options={{ headerShown: false }}
            />

            {/* Full-screen screens that should NOT have tab bar accessible on mobile */}
            <RootStack.Screen
              name="Stream"
              component={MobileStream}
              options={{
                headerShown: Platform.OS === "web" && !socialShell,
                headerTitle: "",
              }}
            />
            <RootStack.Screen
              name="MobileGoLive"
              component={MobileGoLive}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="Video"
              component={VideoScreen}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="Vod"
              component={VodScreen}
              options={{ headerShown: false }}
            />

            {/* Utility/embed screens */}
            <RootStack.Screen
              name="AppReturn"
              component={AppReturnScreen}
              options={{ title: "Returning to app..." }}
            />
            <RootStack.Screen
              name="PopoutChat"
              component={PopoutChat}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="Embed"
              component={EmbedScreen}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="VodEmbed"
              component={VodEmbedScreen}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="InfoWidgetEmbed"
              component={InfoWidgetEmbed}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="DanmuOBS"
              component={DanmuOBSScreen}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="PopoutStreamMonitor"
              component={PopoutStreamMonitor}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="PopoutInfoWidget"
              component={PopoutInfoWidget}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="PopoutMultistream"
              component={PopoutMultistream}
              options={{ headerShown: false }}
            />
            <RootStack.Screen
              name="PopoutLivestream"
              component={PopoutLivestream}
              options={{ headerShown: false }}
            />
          </RootStack.Navigator>
        </View>
      </Animated.View>
      {/* Scrim behind the social shell's phone menu drawer */}
      {!isNative && socialShell && !sidebar.isActive && mobileMenuOpen && (
        <Pressable
          accessibilityLabel="Close menu"
          onPress={closeDrawer}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#000", // token-ok: overlay scrim
            opacity: 0.55,
            zIndex: 127000,
          }}
        />
      )}
      {/* Scrim behind the overlay drawer (detail views only) */}
      {!isNative && (
        <AnimatedPressable
          accessibilityLabel="Close menu"
          pointerEvents={
            sidebar.overlay && sidebar.drawerOpen ? "auto" : "none"
          }
          onPress={closeDrawer}
          style={[
            scrimStyle,
            {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#000", // token-ok: overlay scrim
              zIndex: 127000,
            },
          ]}
        />
      )}
      {/* Header cluster on detail views: hamburger + logo, sitting exactly where
          the drawer's brand row will appear so it stays put as the drawer opens. */}
      {!isNative &&
        sidebar.isActive &&
        sidebar.overlay &&
        !sidebar.drawerOpen && (
          <View
            style={[
              {
                position: "absolute",
                top: 0,
                height: 56,
                flexDirection: "row",
                alignItems: "center",
                // Match the sidebar brand row (gap 0) so the logo sits in the
                // same spot when navigating between the sidebar and detail-view
                // headers.
                gap: 0,
                zIndex: 128001,
              },
              isVodDetail
                ? // Full-width liquid-glass bar the video scrolls under.
                  ({
                    left: 0,
                    right: 0,
                    paddingHorizontal: spacing[2],
                    backgroundColor: withAlpha(z.theme.colors.surface0, 0.55),
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    borderBottomWidth: 1,
                    borderBottomColor: z.theme.colors.borderSubtle,
                  } as any)
                : { left: spacing[2] },
            ]}
          >
            <SidebarToggle label="Open menu" onPress={sidebar.toggle} />
            <LogoBrandMenu>
              <Pressable
                // @ts-ignore renders as <a> on web
                href="/"
                style={{ flexShrink: 1, minWidth: 0 }}
                onPress={(e: any) => {
                  e?.preventDefault?.();
                  navigation.navigate("MainTabs" as any, {
                    screen: "HomeTab",
                    params: { screen: "HomeMain" },
                  });
                }}
              >
                <SiteTitleLockup
                  size={19}
                  weight="semibold"
                  letterSpacing={0}
                  markColor={colors.white}
                  color={colors.white}
                />
              </Pressable>
            </LogoBrandMenu>
          </View>
        )}
      {authModals}
      <UploadProgressIndicator />
    </View>
  );
}
